/**
 * ConsentScripts — dynamically loads analytics/marketing scripts ONLY after
 * the user grants the corresponding consent category. Reacts to consent
 * changes in real-time (accept / reject / preferences) and unloads when
 * consent is withdrawn (best-effort: removes tags + sets `*-disable` flags).
 *
 * Configure via env vars in /app/frontend/.env:
 *   REACT_APP_GA_ID            (e.g. G-XXXXXXXXXX) — analytics category
 *   REACT_APP_GTM_ID           (e.g. GTM-XXXXXXX) — marketing category
 *   REACT_APP_META_PIXEL_ID    (Meta Pixel) — marketing category
 *
 * If a key is not set the script is simply skipped (no errors logged).
 */
import { useEffect } from "react";
import { getConsent, CONSENT_EVENTS } from "@/lib/cookies";

const GA_ID = process.env.REACT_APP_GA_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

const TAG_ATTR = "data-gv-consent";

function injectScript({ id, src, inline, category, async = true }) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[data-gv-id="${id}"]`)) return;
  const s = document.createElement("script");
  s.setAttribute("data-gv-id", id);
  s.setAttribute(TAG_ATTR, category);
  if (src) {
    s.src = src;
    s.async = async;
  } else if (inline) {
    s.text = inline;
  }
  document.head.appendChild(s);
}

function removeScripts(category) {
  if (typeof document === "undefined") return;
  document.querySelectorAll(`script[${TAG_ATTR}="${category}"]`).forEach((el) => el.remove());
}

function loadAnalytics() {
  if (!GA_ID) return;
  // Google Analytics 4 (gtag)
  injectScript({
    id: `ga-${GA_ID}`,
    src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
    category: "analytics",
  });
  injectScript({
    id: "ga-init",
    inline: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
gtag('js', new Date());
gtag('config', '${GA_ID}', { anonymize_ip: true });`,
    category: "analytics",
  });
}

function unloadAnalytics() {
  removeScripts("analytics");
  // GA opt-out flag
  if (GA_ID && typeof window !== "undefined") {
    window[`ga-disable-${GA_ID}`] = true;
  }
}

function loadMarketing() {
  // Google Tag Manager
  if (GTM_ID) {
    injectScript({
      id: `gtm-${GTM_ID}`,
      inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;j.setAttribute('${TAG_ATTR}','marketing');f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`,
      category: "marketing",
    });
  }
  // Meta Pixel
  if (META_PIXEL_ID) {
    injectScript({
      id: `meta-${META_PIXEL_ID}`,
      inline: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.setAttribute('${TAG_ATTR}','marketing');t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${META_PIXEL_ID}');
fbq('track', 'PageView');`,
      category: "marketing",
    });
  }
}

function unloadMarketing() {
  removeScripts("marketing");
}

function applyConsent(c) {
  if (!c) return;
  if (c.analytics) loadAnalytics(); else unloadAnalytics();
  if (c.marketing) loadMarketing(); else unloadMarketing();
}

const ConsentScripts = () => {
  useEffect(() => {
    // Apply consent state at mount (does nothing if user hasn't decided yet
    // — banner will eventually trigger a CHANGE event)
    applyConsent(getConsent());

    const onChange = (e) => applyConsent(e.detail || getConsent());
    window.addEventListener(CONSENT_EVENTS.CHANGE, onChange);
    return () => window.removeEventListener(CONSENT_EVENTS.CHANGE, onChange);
  }, []);

  return null;
};

export default ConsentScripts;
